import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import Particles, { initParticlesEngine } from '@tsparticles/react';
import { loadSlim } from '@tsparticles/slim';
import AZ from '../../assets/logos/az.png';
import CD1 from '../../assets/images/CardM.jpg';
import CD2 from '../../assets/images/CardM2.jpg';
import CD3 from '../../assets/images/CardM3.jpg';

// Palette white / gold
const colors = {
  bg: '#FFFFFF',
  gold: '#C69C6D',
  goldDark: '#B88A56',
  goldLight: '#F5E1C8',
  text: '#3B2F2A',
  textSoft: '#5C4A3D',
  glassBg: 'rgba(255, 255, 255, 0.7)',
  glassBorder: 'rgba(198, 156, 109, 0.25)',
  shadow: '0 8px 24px rgba(107, 90, 70, 0.06)',
  shadowHover: '0 16px 32px rgba(184, 138, 86, 0.15)',
};

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.15, delayChildren: 0.2 },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: 'easeOut' } },
};

const MainContainerAZLG2 = () => {
  const [engineReady, setEngineReady] = useState(false);

  useEffect(() => {
    initParticlesEngine(async (engine) => {
      await loadSlim(engine);
    }).then(() => setEngineReady(true));
  }, []);

  const particlesOptions = {
    background: { color: 'transparent' },
    fpsLimit: 60,
    particles: {
      number: { value: 60, density: { enable: true, area: 800 } },
      color: { value: ['#C69C6D', '#E2B88A', '#B88A56', '#F5E1C8'] },
      shape: { type: 'circle' },
      opacity: {
        value: { min: 0.2, max: 0.5 },
        anim: { enable: true, speed: 1, minimumValue: 0.1, sync: false },
      },
      size: {
        value: { min: 1, max: 4 },
        random: true,
        anim: { enable: true, speed: 2, minimumValue: 0.5, sync: false },
      },
      links: { enable: false },
      move: {
        enable: true,
        speed: { min: 0.3, max: 1.2 },
        direction: 'none',
        random: true,
        outModes: 'bounce',
      },
    },
    interactivity: {
      events: {
        onHover: { enable: true, mode: 'bubble' },
      },
      modes: {
        bubble: { distance: 200, size: 6, duration: 2, opacity: 0.8 },
      },
    },
    detectRetina: true,
  };

  const cards = [
    {
      img: CD1,
      title: 'FULL ACCES',
      price: '$60',
      features: [
        'Service protocolaire',
        'Buffet à volonté',
        'Une boisson au choix (Soft Drink)',
        'Accès confortable à notre salon privé',
        'Connexion Wi-Fi gratuite',
      ],
      highlight: '✨ Prise en charge complète dès votre arrivée jusqu\'à votre départ.',
    },
    {
      img: CD2,
      title: 'ACCES CLASSIQUE',
      price: '$35',
      features: [
        'Buffet à volonté',
        'Une boisson au choix (Soft Drink)',
        'Accès confortable à notre salon privé',
        'Connexion Wi-Fi gratuite',
      ],
      highlight: '✨ Restauration premium : buffet open et une boisson au choix (Soft Drink).',
    },
    {
      img: CD3,
      title: 'FORMULE PETIT DEJEUNER',
      price: '$20',
      features: [
        'Petit Déjeuner',
        'Accès confortable à notre salon privé',
        'Connexion Wi-Fi gratuite',
      ],
      highlight: '✨ Savourez un petit-déjeuner classique avec du bon pain chaud, des œufs au plat, jus frais et café.',
    },
  ];

  return (
    <>
      <style>{`
        .azlg2-section {
          position: relative;
          background: ${colors.bg};
          padding: 100px 20px;
          overflow: hidden;
          text-align: center;
        }

        /* Halos dorés */
        .azlg2-section::before,
        .azlg2-section::after {
          content: '';
          position: absolute;
          border-radius: 50%;
          filter: blur(120px);
          opacity: 0.15;
          z-index: 0;
          pointer-events: none;
        }
        .azlg2-section::before {
          width: 50vw;
          height: 50vw;
          background: ${colors.goldLight};
          top: -10%;
          left: -5%;
          animation: floatOrb 18s ease-in-out infinite;
        }
        .azlg2-section::after {
          width: 40vw;
          height: 40vw;
          background: #EAD5C0;
          bottom: -8%;
          right: -4%;
          animation: floatOrb 15s ease-in-out infinite alternate-reverse;
        }

        @keyframes floatOrb {
          0% { transform: translate(0, 0) scale(1); }
          50% { transform: translate(5%, 10%) scale(1.15); }
          100% { transform: translate(-5%, 5%) scale(0.9); }
        }

        .container {
          position: relative;
          z-index: 2;
          max-width: 1200px;
          margin: 0 auto;
        }

        #tsparticles {
          position: absolute;
          inset: 0;
          z-index: 1;
          pointer-events: none;
        }

        .logo {
          width: 220px;
          margin-bottom: 30px;
          filter: drop-shadow(0 8px 16px rgba(0,0,0,0.05));
        }

        .section-title {
          font-size: clamp(2rem, 5vw, 2.8rem);
          font-weight: 700;
          color: ${colors.text};
          margin-bottom: 40px;
          letter-spacing: -0.5px;
          position: relative;
        }
        .section-title::after {
          content: '';
          display: block;
          width: 80px;
          height: 3px;
          background: linear-gradient(90deg, ${colors.gold}, ${colors.goldDark});
          margin: 15px auto 0;
          border-radius: 2px;
        }

        .cards-grid {
          display: flex;
          flex-wrap: wrap;
          justify-content: center;
          gap: 24px;
        }

        /* Flip card container */
        .flip-card {
          background: transparent;
          width: 320px;
          height: 500px;
          perspective: 1000px;
          cursor: pointer;
        }

        .flip-card-inner {
          position: relative;
          width: 100%;
          height: 100%;
          transition: transform 0.6s ease;
          transform-style: preserve-3d;
        }

        .flip-card:hover .flip-card-inner {
          transform: rotateY(180deg);
        }

        .flip-card-front, .flip-card-back {
          position: absolute;
          width: 100%;
          height: 100%;
          backface-visibility: hidden;
          border-radius: 24px;
          overflow: hidden;
          box-shadow: ${colors.shadow};
          border: 1px solid ${colors.glassBorder};
        }

        .flip-card-front {
          background: ${colors.glassBg};
          backdrop-filter: blur(18px);
          -webkit-backdrop-filter: blur(18px);
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .flip-card-front img {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }

        .flip-card-back {
          background: ${colors.glassBg};
          backdrop-filter: blur(20px);
          -webkit-backdrop-filter: blur(20px);
          transform: rotateY(180deg);
          padding: 25px 20px;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          color: ${colors.text};
        }

        .card-title {
          font-size: 1.4rem;
          font-weight: 700;
          margin-bottom: 15px;
        }

        .features-list {
          list-style: none;
          padding: 0;
          text-align: left;
          margin-bottom: 15px;
        }

        .features-list li {
          font-size: 0.95rem;
          color: ${colors.textSoft};
          margin-bottom: 8px;
          display: flex;
          align-items: baseline;
        }

        .features-list li::before {
          content: "•";
          color: ${colors.gold};
          font-weight: bold;
          margin-right: 8px;
        }

        .price {
          font-size: 2.5rem;
          font-weight: 700;
          color: ${colors.goldDark};
          margin: 10px 0;
        }

        .highlight {
          font-size: 0.85rem;
          background: rgba(198, 156, 109, 0.15);
          padding: 8px 12px;
          border-radius: 16px;
          color: ${colors.textSoft};
          margin-top: 10px;
        }

        @media (max-width: 768px) {
          .flip-card {
            width: 280px;
            height: 450px;
          }
        }
      `}</style>

      <section className="azlg2-section">
        {engineReady && <Particles id="tsparticles" options={particlesOptions} />}

        <div className="container">
          <motion.div
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
          >
            {/* Logo */}
            <motion.div variants={itemVariants}>
              <motion.img
                src={AZ}
                alt="Azyekos Logo"
                className="logo"
                animate={{ y: [0, -10, 0] }}
                transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut' }}
              />
            </motion.div>

            {/* Titre */}
            <motion.h1 className="section-title" variants={itemVariants}>
              ACCES SALON
            </motion.h1>

            {/* Cartes */}
            <motion.div className="cards-grid" variants={itemVariants}>
              {cards.map((card, index) => (
                <motion.div
                  key={index}
                  className="flip-card"
                  variants={{
                    hidden: { opacity: 0, y: 20 },
                    visible: { opacity: 1, y: 0 },
                  }}
                >
                  <div className="flip-card-inner">
                    <div className="flip-card-front">
                      <img src={card.img} alt={card.title} />
                    </div>
                    <div className="flip-card-back">
                      <h3 className="card-title">{card.title}</h3>
                      <ul className="features-list">
                        {card.features.map((feat, i) => (
                          <li key={i}>{feat}</li>
                        ))}
                      </ul>
                      <div className="price">{card.price}</div>
                      <p className="highlight">{card.highlight}</p>
                    </div>
                  </div>
                </motion.div>
              ))}
            </motion.div>
          </motion.div>
        </div>
      </section>
    </>
  );
};

export default MainContainerAZLG2;